import {
  Checkbox,
  ComboBox,
  DefaultButton,
  IComboBoxProps,
  IDatePickerStrings,
  PrimaryButton,
  TextField,
  defaultDatePickerStrings
} from "@fluentui/react";
import { Field, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { AsyncSubject } from "rxjs";
import { JemConfiguration } from "../../../../JemConfiguration";
import { convertToRecurringScheduleFrequencyOptions, LoadingStatus } from "../../../../Shared";
import { DatePickerField } from "../../../../Shared/components/DatePickerField/DatePickerField";
import { SimpleShimmerForForms } from "../../../../Shared/components/SimpleShimmerForForms/SimpleShimmerForForms";
import { DomainDataEnum, DomainDataObjects } from "../../../../Shared/contexts/JEMContext/JEMContext.domainData.types";
import { ForceMaxWidthAndHeightOnDropdownStyle } from "../../../../Shared/utilities/FluentUIHacks";
import { FCWChecklistModel } from "../../../utilities";
import { GLPageState } from "../GLCreate.State";
import { useConst } from "@fluentui/react-hooks";
import { GLCreateFormikState } from "../GLCreateForm.types";
import { AdditionalData } from "../../../shared";
import { GLCreateActionsEnum } from "../useWeightage";

export interface GLCreateRecurringJeDetailsProps {
  configuration: JemConfiguration["GeneralLedgerApi"];
  pageState: GLPageState;
  disabled: boolean;
  loadingStatus: LoadingStatus;
  domainData: Pick<DomainDataObjects, DomainDataEnum.RecurringScheduleFrequency>;
  onFiscalPeriodChange: (confirmation: AsyncSubject<boolean>, fiscalPeriod: string) => void;
  onChecklistChange: (confirmation: AsyncSubject<boolean>, checklist: FCWChecklistModel) => void;
  onPostingDateChange: (confirmation: AsyncSubject<boolean>, postingDate: Date) => void;
  openHistoryPanel?: (guid: string | number) => void;
  onAction?: (actionName: GLCreateActionsEnum, options?: AdditionalData) => void;
}

export const dateStyles = {
  position: "absolute",
  top: "32px",
  right: "35px",
  border: "none",
  minWidth: "auto",
  padding: "5px",
  height: "auto"
};

// Custom styles for better disabled field readability
const disabledFieldStyles = {
  fieldGroup: {
    backgroundColor: "#f8f8f8", // Light gray background instead of very light
    border: "1px solid #d2d0ce", // Slightly darker border
    "&:hover": {
      borderColor: "#d2d0ce !important" // Prevent hover border highlight
    }
  },
  field: {
    color: "#605e5c !important", // Darker text color for better readability
    backgroundColor: "#f8f8f8"
  },
  label: {
    color: "#323130 !important" // Ensure labels remain readable
  }
};

const disabledComboBoxStyles = {
  root: {
    "& .ms-ComboBox-Input": {
      color: "#605e5c !important", // Darker text for disabled ComboBox
      backgroundColor: "#f8f8f8 !important"
    },
    "&:hover .ms-ComboBox-Input": {
      borderColor: "#d2d0ce !important" // Prevent hover border highlight
    }
  },
  input: {
    color: "#605e5c !important",
    backgroundColor: "#f8f8f8 !important"
  }
};

// More specific DatePicker styles that should work with custom DatePickerField component
const disabledDatePickerStyles = {
  root: {
    "& .ms-TextField-fieldGroup": {
      backgroundColor: "#f8f8f8 !important",
      border: "1px solid #d2d0ce !important"
    },
    "& .ms-TextField-field": {
      color: "#605e5c !important",
      backgroundColor: "#f8f8f8 !important"
    },
    "& input[disabled]": {
      color: "#605e5c !important",
      backgroundColor: "#f8f8f8 !important"
    }
  },
  textField: {
    fieldGroup: {
      backgroundColor: "#f8f8f8",
      border: "1px solid #d2d0ce",
      "&:hover": {
        borderColor: "#d2d0ce !important" // Prevent hover border highlight
      }
    },
    field: {
      color: "#605e5c",
      backgroundColor: "#f8f8f8"
    }
  }
};

// Create a style tag for global CSS to override label styling
if (typeof document !== "undefined") {
  const styleId = "datepicker-label-override";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .datepicker-label-override .ms-Label {
        color: #323130 !important;
      }
      .datepicker-label-override label {
        color: #323130 !important;
      }
    `;
    document.head.appendChild(style);
  }
}

const GLCreateRecurringJeDetails: React.FC<GLCreateRecurringJeDetailsProps> = (props) => {
  const formik = useFormikContext<GLCreateFormikState>();
  const createState = props.pageState.createState;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate());

  const frequencyDropdownOptions = convertToRecurringScheduleFrequencyOptions(
    props.domainData.RecurringScheduleFrequency || []
  );

  const [isRecurringJE, setIsRecurringJE] = useState(false);

  useEffect(() => {
    if (
      formik.values.recurringDetailsTabFrequency &&
      formik.values.recurringDetailsTabScheduleName &&
      formik.values.recurringDetailsTabStartDate &&
      formik.values.recurringDetailsTabEndDate
    ) {
      setIsRecurringJE(true);
    } else {
      setIsRecurringJE(false);
    }
  }, [
    formik.values.recurringDetailsTabFrequency,
    formik.values.recurringDetailsTabScheduleName,
    formik.values.recurringDetailsTabStartDate,
    formik.values.recurringDetailsTabEndDate
  ]);

  useEffect(() => {
    formik.setFieldValue("recurringDetailsTabNextPostingDate", formik.values.detailsTabPostingDate);
  }, [formik.values.detailsTabPostingDate]);

  useEffect(() => {
    if (formik.values.recurringDetailsTabEndDate) {
      const endDate = new Date(formik.values.recurringDetailsTabEndDate).getTime();
      const today = new Date().getTime();
      formik.setFieldValue("recurringDetailsTabIsLongTerm", Math.abs(endDate - today) / (1000 * 60 * 60 * 24) > 365);
    }
  }, [formik.values.recurringDetailsTabEndDate]);

  // useEffect(() => {
  //   if (
  //     formik.values.recurringDetailsTabEndDate &&
  //     formik.values.recurringDetailsTabStartDate &&
  //     formik.values.recurringDetailsTabEndDate < formik.values.recurringDetailsTabStartDate
  //   ) {
  //     formik.setFieldError("recurringDetailsTabEndDate", "Invalid End Date. Start date should be before the end date.");
  //   }
  //   if (formik.values.recurringDetailsTabFrequency && formik.values.recurringDetailsTabStartDate) {
  //     // set posting date
  //     formik.setFieldValue("recurringDetailsTabNextPostingDate", formik.values.recurringDetailsTabStartDate);
  //   }
  // }, [
  //   formik.values.recurringDetailsTabFrequency,
  //   formik.values.recurringDetailsTabStartDate,
  //   formik.values.recurringDetailsTabEndDate
  // ]);

  const outOfBoundsErrorMessage: IDatePickerStrings = useConst(() => ({
    ...defaultDatePickerStrings,
    isOutOfBoundsErrorMessage: "Posting Date should be in the current Posting Period."
  }));

  const onFrequencyChange =
    (fieldName: string): IComboBoxProps["onChange"] =>
    (_event, item, _index, _value) => {
      if (!item) return;
      formik.setFieldValue(fieldName, item.key);
    };

  return (
    <>
      <div className="ms-Grid" dir="ltr">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <b>Populate the fields on this page ONLY if you want this JE to be part of a recurring JE schedule</b>
          {!!formik.values.recurringDetailsTabRJEScheduleId && (
            <PrimaryButton
              onClick={() => {
                props.openHistoryPanel && props.openHistoryPanel(formik.values.recurringDetailsTabRJEScheduleId);
              }}
            >
              History
            </PrimaryButton>
          )}
        </div>
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg6 ms-xl4">
            <div className="ms-Grid" dir="ltr">
              <div className="ms-Grid-row">
                <div className={props.disabled || createState.isExternalDraft ? "datepicker-label-override" : ""}>
                  <SimpleShimmerForForms loadingStatus={props.loadingStatus} height={32} label="Frequency">
                    <Field
                      label="Frequency"
                      name={"recurringDetailsTabFrequency"}
                      onChange={
                        props.disabled || createState.isExternalDraft
                          ? () => {
                              /* disabled */
                            }
                          : onFrequencyChange("recurringDetailsTabFrequency")
                      }
                      as={ComboBox}
                      options={frequencyDropdownOptions}
                      styles={{
                        ...ForceMaxWidthAndHeightOnDropdownStyle({ width: "100%", height: "auto" }),
                        ...(props.disabled || createState.isExternalDraft ? disabledComboBoxStyles : {})
                      }}
                      allowFreeform={true}
                      autoComplete="on"
                      selectedKey={
                        formik.values.recurringDetailsTabFrequency ? formik.values.recurringDetailsTabFrequency : ""
                      }
                      disabled={props.disabled || createState.isExternalDraft}
                      errorMessage={props.disabled ? "" : formik.errors.recurringDetailsTabFrequency}
                      iconButtonProps={{ "aria-label": "Toggle frequency dropdown", tabIndex: -1 }}
                    />
                  </SimpleShimmerForForms>
                </div>
              </div>
              <div className="ms-Grid-row" style={{ position: "relative" }}>
                <div className={props.disabled ? "datepicker-label-override" : ""}>
                  <SimpleShimmerForForms loadingStatus={props.loadingStatus} height={32} label="Start Date">
                    <Field
                      name="recurringDetailsTabStartDate"
                      label="Start Date"
                      component={DatePickerField}
                      minDate={tomorrow}
                      // maxDate={new Date(formik.values.detailsTabPostingDate)}
                      strings={outOfBoundsErrorMessage}
                      disabled={props.disabled}
                      errorMessage={
                        formik.errors.recurringDetailsTabStartDate ? formik.errors.recurringDetailsTabStartDate : ""
                      }
                      styles={props.disabled ? disabledDatePickerStyles : {}}
                      textField={props.disabled ? disabledDatePickerStyles.textField : undefined}
                    />
                    <DefaultButton
                      iconProps={{ iconName: "Refresh" }}
                      ariaLabel="Refresh"
                      ariaDescription="Clear the start date"
                      onClick={() => {
                        formik.setFieldValue("recurringDetailsTabStartDate", "");
                      }}
                      style={dateStyles}
                      disabled={props.disabled}
                    ></DefaultButton>
                  </SimpleShimmerForForms>
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="datepicker-label-override">
                  <SimpleShimmerForForms loadingStatus={props.loadingStatus} height={32} label="Next Posting Date">
                    <Field
                      name="recurringDetailsTabNextPostingDate"
                      label="Next Posting Date"
                      component={DatePickerField}
                      strings={outOfBoundsErrorMessage}
                      disabled={true}
                      styles={disabledDatePickerStyles}
                      textField={disabledDatePickerStyles.textField}
                    />
                  </SimpleShimmerForForms>
                </div>
              </div>
              <div className="ms-Grid-row" style={{ position: "relative" }}>
                <div className={props.disabled ? "datepicker-label-override" : ""}>
                  <SimpleShimmerForForms loadingStatus={props.loadingStatus} height={32} label="End Date">
                    <Field
                      name="recurringDetailsTabEndDate"
                      label="End Date"
                      component={DatePickerField}
                      minDate={formik.values.recurringDetailsTabStartDate}
                      strings={outOfBoundsErrorMessage}
                      disabled={props.disabled}
                      errorMessage={
                        formik.errors.recurringDetailsTabEndDate ? formik.errors.recurringDetailsTabEndDate : ""
                      }
                      required
                      styles={props.disabled ? disabledDatePickerStyles : {}}
                      textField={props.disabled ? disabledDatePickerStyles.textField : undefined}
                    />
                    <DefaultButton
                      iconProps={{ iconName: "Refresh" }}
                      ariaLabel="Refresh"
                      ariaDescription="Clear the end date"
                      onClick={() => {
                        formik.setFieldValue("recurringDetailsTabEndDate", "");
                      }}
                      style={dateStyles}
                      disabled={props.disabled}
                    ></DefaultButton>
                  </SimpleShimmerForForms>
                </div>
              </div>
              <div className="ms-Grid-row">
                <SimpleShimmerForForms
                  loadingStatus={props.loadingStatus}
                  height={32}
                  label="Recurring JE Schedule Name"
                >
                  <Field
                    as={TextField}
                    label="Recurring JE Schedule Name"
                    name="recurringDetailsTabScheduleName"
                    type="input"
                    disabled={false}
                    readOnly={props.disabled || formik.values.recurringDetailsTabRJEScheduleId}
                    styles={props.disabled || formik.values.recurringDetailsTabRJEScheduleId ? disabledFieldStyles : {}}
                  />
                </SimpleShimmerForForms>
              </div>
              <div className="ms-Grid-row" style={{ display: "flex", alignItems: "baseline" }}>
                <SimpleShimmerForForms loadingStatus={props.loadingStatus} height={32}>
                  <Field
                    label="Transfer amount in local currency"
                    name={"recurringDetailsTabIsLocalCurrency"}
                    type="checkbox"
                    as={Checkbox}
                    styles={{
                      root: {
                        paddingTop: "10px"
                      }
                    }}
                    onChange={(e, checked) => {
                      if (checked) formik.setFieldValue("templateid", "2");
                      else formik.setFieldValue("templateid", "1");
                      props.onAction && props.onAction(GLCreateActionsEnum.Save);
                    }}
                    disabled={props.disabled || !isRecurringJE}
                  />
                </SimpleShimmerForForms>
              </div>
              <div className="ms-Grid-row">
                <SimpleShimmerForForms loadingStatus={props.loadingStatus} height={32}>
                  <Field
                    label="Long Term"
                    name={"recurringDetailsTabIsLongTerm"}
                    type="checkbox"
                    as={Checkbox}
                    styles={{
                      root: {
                        paddingTop: "10px"
                      }
                    }}
                    disabled={true}
                  />
                </SimpleShimmerForForms>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

GLCreateRecurringJeDetails.displayName = "GLCreateRecurringJeDetails";

export { GLCreateRecurringJeDetails };
